/**
 * Product Media Management System - Moon Moon Agency
 * Handles media display, variant-specific images, and slider interactions
 */

/* URL d'ENTRÉE : capturée par mm_main_product.js (chargé avant) AVANT que updateURL ne réécrive
   ?color=. Fallback sur l'URL courante si ce module est chargé seul. Sert à savoir si
   l'utilisateur est arrivé avec une couleur forcée → la galerie se filtre dès l'arrivée. */
const MM_ENTRY_SEARCH = window.MM_ENTRY_SEARCH ?? window.location.search;

/*********************************************************************************************
 * ⚙️ CONFIGURATION
 */
const MEDIA_CONFIG = {
	imageWidths: {
		main: 1000,
		thumbnail: 500
	},
	
	defaultMaxImages: 2,
	variantMaxImages: 8,

	// Selectors
	selectors: {
		productMedias: '[data-product-medias] [data-splide-slide]',
		variantMediaData: '#variant-media-data',
		variantMediaIds: '#variant-media-ids',
		variantInfoModele: '#variant-info-modele',
		infoModele: '.mm-media-info-modele',
		mainSlider: '[data-main-slider]:not([data-variant-slider])',
		mainSliderVariant: '[data-main-slider][data-variant-slider]',
		thumbnailSliderVariant: '[data-thumbnail-slider][data-variant-slider]',
		splideSlide: '[data-splide-slide]',
		splideList: '[data-splide-list]',
		slideImage: 'img',
		dataId: '[data-id="{{id}}"]'
	},
	
	// CSS Classes
	classes: {
		mainProductMediaElem: 'mm-main-product-media-elem',
		thumbnailMedia: 'mm-thumbnail-media'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */

// Media Event Emitter
const mediaEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'media'
	};
	
	return document.dispatchEvent(new CustomEvent(`product:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

/*********************************************************************************************
 * 📸 MEDIA MANAGEMENT
 */
const MediaManager = {
	updateMedia(variant) {
		if (!variant?.featured_media?.id) return;

		try {
			const variantMediaId = variant.featured_media.id;
			const allMainMedia = document.querySelectorAll(MEDIA_CONFIG.selectors.productMedias);

			if (!allMainMedia.length) {
				window.log('info', 'MediaManager', 'No media slides found');
				return;
			}

			// Find the media slide with matching ID
			const targetSlideIndex = Array.from(allMainMedia).findIndex(media => {
				const mediaId = media.dataset.id;
				return mediaId && parseInt(mediaId, 10) === variantMediaId;
			});

			// Navigate to the slide if found and splide instance exists
			if (targetSlideIndex !== -1) {
				if (window.splideMainInstance && typeof window.splideMainInstance.go === 'function') {
					window.splideMainInstance.go(targetSlideIndex);
				} else {
					window.log('info', 'MediaManager', 'Splide instance not available or invalid');
				}
			} else {
				window.log('info', 'MediaManager', 'Target slide not found for media ID', { variantMediaId });
			}
			
		} catch (error) {
			window.log('error', 'MediaManager', 'Failed to update media', { error: error.message });
		}
	},

	/**
	 * Update variant images
	 */
	// Tant que l'utilisateur n'a pas explicitement choisi une couleur (clic pastille / URL
	// ?color=), la galerie standard montre TOUTES les images produit. On ne filtre sur la
	// couleur qu'après cette 1ère interaction — les `change` programmatiques de l'init
	// (ajustement de taille, pré-sélection 1ère pastille) ne doivent pas filtrer.
	userSelectedColor: false,

	updateImagesVariant(variant) {
		if (!variant?.id) return;

		const variantId = variant.id.toString();
		mediaEmit('variant-media-switch-start', { variantId });

		// Info modèle par couleur : réécrit le texte de l'overlay pour le variant courant.
		this._updateInfoModele(variantId);

		// Check if we have variant-specific media data
		const variantMediaData = this.getVariantMediaData();

		if (variantMediaData && variantMediaData[variantId]) {
			// Handle variant-specific images (complex case)
			this.handleVariantSpecificMedia(variantId, variantMediaData);
		} else if (this.userSelectedColor) {
			// Handle standard product - filtrer sur la couleur (seulement après interaction)
			this.handleStandardProductMedia(variant);
		}
	},

	/**
	 * Get variant media data
	 */
	getVariantMediaData() {
		try {
			const dataScript = document.querySelector(MEDIA_CONFIG.selectors.variantMediaData);
			if (!dataScript) return null;
			return JSON.parse(dataScript.textContent);
		} catch (error) {
			window.log('error', 'MediaManager', 'Failed to parse variant media data', { error: error.message });
			return null;
		}
	},

	/**
	 * Handle variant-specific media replacement
	 */
	handleVariantSpecificMedia(variantId, variantMediaData) {
		try {
			const entry = variantMediaData[variantId];
			const variantMedia = entry.media;

			if (!Array.isArray(variantMedia) || variantMedia.length === 0) {
				window.log('error', 'MediaManager', 'Invalid variant media data', { variantId, variantMedia });
				mediaEmit('variant-media-switch-failed', { variantId, reason: 'invalid-media-data' });
				return;
			}

			const extra = Array.isArray(entry.extra) ? entry.extra : [];
			const allMedia = variantMedia.concat(extra);

			this.updateSliderContent(variantId, allMedia);
			mediaEmit('variant-media-switch-success', { variantId, type: 'variant-specific' });
		} catch (error) {
			window.log('error', 'MediaManager', 'Failed to update variant images', { error: error.message });
			mediaEmit('variant-media-switch-error', { variantId, error: error.message });
		}
	},

	/**
	 * Handle standard product — filter desktop stack to show only variant images, navigate mobile slider to first
	 */
	handleStandardProductMedia(variant) {
		try {
			const variantId = variant.id.toString();

			// Desktop : show only images belonging to this variant
			const { mediaIds, featuredId, extraCount } = this.getVariantMediaInfo(variantId);
			this._applyDesktopFilter(mediaIds, variantId, extraCount, featuredId);

			// Mobile : même règle que le desktop — uniquement les images de la couleur.
			this._applyMobileFilter(variantId, mediaIds, featuredId, extraCount);

			mediaEmit('variant-media-switch-success', { variantId, type: 'standard-filter' });
		} catch (error) {
			window.log('error', 'MediaManager', 'Failed to filter variant media', { error: error.message });
			mediaEmit('variant-media-switch-error', { variantId: variant.id.toString(), error: error.message });
		}
	},

	/**
	 * Filtre le slider mobile sur la couleur du variant — même règle que le desktop
	 * (_applyDesktopFilter) : on n'affiche QUE les images de la couleur (featured, médias
	 * rattachés, extra metaobject). Sans ça, le swipe mobile faisait défiler les images
	 * des autres coloris alors qu'une seule couleur est sélectionnée.
	 */
	_applyMobileFilter(variantId, mediaIds = [], featuredId = null, extraCount = 0) {
		const mobileSlider = document.querySelector('[data-main-slider-mobile]:not([data-variant-slider])');
		const sliderPair = window.ProductMediaSliders?.instances?.['main'];
		if (!mobileSlider || !sliderPair?.main) return;

		if (extraCount > 0 || mediaIds.length > 0 || featuredId) {
			// Un variant avec seulement une featured (ni média rattaché, ni extra) doit afficher
			// ce seul slide : restaurer tous les slides produit laisserait swiper sur les autres coloris.
			this._applyMobileExtraSlides(variantId, mobileSlider, sliderPair, featuredId, mediaIds);
		} else {
			// Variant sans aucune image propre : fallback sur les premières images du produit,
			// bornées à defaultMaxImages comme le desktop (_applyDesktopFilter). Sans la borne,
			// le mobile restaurait TOUS les slides du produit (464 variants Emma → 150 slides)
			// alors que le desktop n'en montre que 2.
			this._applyMobileDefaultSlides(mobileSlider, sliderPair, variantId);
		}
	},

	/**
	 * Swap mobile Splide slides with the extra metaobject images of a variant.
	 * Uses destroy/mount cycle so Splide fully re-initialises with the new slides.
	 */
	_applyMobileExtraSlides(variantId, mobileSlider, sliderPair, featuredId = null, mediaIds = []) {
		if (!mobileSlider || !sliderPair?.main) return;

		// Ensure this variant's extra images exist in the DOM (mobile reads the same container)
		this._ensureExtraDesktopItems(variantId);

		const extraItems = document.querySelectorAll(`[data-extra-slides-desktop] [data-id^="meta-${variantId}-"]`);

		const list = mobileSlider.querySelector('[data-splide-list]');
		if (!list) return;

		// Save original slides HTML once (before any swap)
		if (!list.dataset.originalHtml) {
			list.dataset.originalHtml = list.innerHTML;
		}

		// Les slides d'origine (source des featured/mediaIds — le DOM courant peut être déjà swappé).
		const originalList = document.createElement('ul');
		originalList.innerHTML = list.dataset.originalHtml;

		let slideIndex = 0;
		const buildSlideFrom = (sourceSlide, id, eager) => {
			const img = sourceSlide?.querySelector('img');
			if (!img) return '';
			const newImg = img.cloneNode(true);
			newImg.className = 'mm-main-product-media-elem mm-w-full mm-h-full';
			newImg.loading = eager ? 'eager' : 'lazy';
			// L'overlay info_modele n'est JAMAIS cloné depuis le slide source : celui-ci vient
			// d'un snapshot HTML figé (originalHtml) qui porte le texte du variant SSR initial
			// et sa position d'origine. On le régénère pour le variant courant, sur la 2e slide.
			const index = slideIndex++;
			return `<li class="splide__slide" data-splide-slide data-id="${id}" data-index="${index}">${newImg.outerHTML}${this._infoModeleSlideHtml(variantId, index)}</li>`;
		};

		// Featured d'abord, puis les slides produit rattachées à la couleur (images MO
		// dédupliquées — mediaIds), puis les extra metaobject injectées.
		let slidesHtml = '';
		if (featuredId) {
			slidesHtml += buildSlideFrom(originalList.querySelector(`[data-id="${featuredId}"]`), featuredId, true);
		}
		mediaIds.forEach(id => {
			if (id === featuredId) return;
			slidesHtml += buildSlideFrom(originalList.querySelector(`[data-id="${id}"]`), id, false);
		});
		Array.from(extraItems).forEach(item => {
			slidesHtml += buildSlideFrom(item, item.dataset.id, false);
		});

		if (!slidesHtml) return;

		this._remountMobileSlider(mobileSlider, list, slidesHtml, sliderPair, 'main');
	},

	/**
	 * Fallback mobile pour un variant sans image propre : ne garder que les premières
	 * images du produit (defaultMaxImages), en miroir du desktop.
	 */
	_applyMobileDefaultSlides(mobileSlider, sliderPair, variantId = null) {
		if (!mobileSlider || !sliderPair?.main) return;
		const list = mobileSlider.querySelector('[data-splide-list]');
		if (!list) return;

		if (!list.dataset.originalHtml) {
			list.dataset.originalHtml = list.innerHTML;
		}

		const originalList = document.createElement('ul');
		originalList.innerHTML = list.dataset.originalHtml;

		const kept = Array.from(originalList.querySelectorAll('[data-splide-slide]'))
			.slice(0, MEDIA_CONFIG.defaultMaxImages);
		if (!kept.length) return;

		const slidesHtml = kept.map((slide, i) => {
			slide.dataset.index = i;
			// Le snapshot porte l'overlay info_modele du variant SSR initial, à sa position
			// d'origine : on le retire et on le régénère pour le variant courant (2e slide).
			slide.querySelectorAll('.mm-media-info-modele').forEach(el => el.remove());
			slide.insertAdjacentHTML('beforeend', this._infoModeleSlideHtml(variantId, i));
			return slide.outerHTML;
		}).join('');

		this._remountMobileSlider(mobileSlider, list, slidesHtml, sliderPair, 'main');
	},

	/**
	 * Destroy Splide, replace slide HTML, re-mount (la pagination native est régénérée par Splide).
	 */
	_remountMobileSlider(mobileSlider, list, slidesHtml, sliderPair, sliderId) {
		try {
			sliderPair.main.destroy();
		} catch (e) { /* ignore */ }

		list.innerHTML = slidesHtml;

		try {
			const config = window.ProductMediaSliders?.config?.main || {};
			const newSplide = new Splide(mobileSlider, config);
			newSplide.mount();
			ArrowContrast.bind(newSplide);
			sliderPair.main = newSplide;
			if (window.ProductMediaSliders?.instances) {
				window.ProductMediaSliders.instances[sliderId] = sliderPair;
			}
		} catch (e) {
			window.log('warn', 'MediaManager', 'Mobile slider remount failed', { error: e.message });
		}
	},

	/**
	 * Show/hide desktop media items. Shows all items if no variant-specific media assigned.
	 * Also shows/hides extra metaobject slides in [data-extra-slides-desktop].
	 */
	/* Rejoue l'animation de fondu d'apparition sur un item média (retrait + reflow + ré-ajout de la classe). */
	_replayFadeIn(item) {
		if (!item) return;
		item.classList.remove('mm-pdp-media-item--fade-in');
		void item.offsetWidth; // force le reflow pour pouvoir rejouer l'animation
		item.classList.add('mm-pdp-media-item--fade-in');
		item.addEventListener('animationend', () => {
			item.classList.remove('mm-pdp-media-item--fade-in');
		}, { once: true });
	},

	_applyDesktopFilter(mediaIds, variantId = null, extraCount = 0, featuredId = null) {
		const desktopSlider = document.querySelector(MEDIA_CONFIG.selectors.mainSlider);
		if (!desktopSlider) return;

		// Inject this variant's extra metaobject images on demand (only the initial variant is SSR)
		if (extraCount > 0 && variantId) {
			this._ensureExtraDesktopItems(variantId);
		}

		const allItems = desktopSlider.querySelectorAll('[data-id]');

		// Le stack desktop est un flex column ordonné par le flux DOM. On réinitialise `order`
		// à chaque filtrage pour repartir de l'ordre naturel, puis on remonte l'image featured
		// du variant en tête (order: -1) — sinon elle reste à sa position DOM (souvent en bas).
		allItems.forEach(item => { item.style.order = ''; });

		/* Affiche/cache un item et joue le fondu d'apparition s'il (ré)apparaît (caché → visible). */
		const reveal = (item, show) => {
			const wasHidden = item.classList.contains('is-hidden-variant') || item.style.display === 'none';
			item.classList.toggle('is-hidden-variant', !show);
			item.style.display = '';
			if (show && wasHidden) this._replayFadeIn(item);
		};

		// Si le variant a des images metaobject : montrer la featured + les slides produit
		// rattachées à la couleur (mediaIds — images MO dédupliquées car déjà dans le slider),
		// cacher le reste. Les extra manquantes sont injectées à part (_ensureExtraDesktopItems).
		if (extraCount > 0) {
			allItems.forEach(item => {
				const show = (featuredId && item.dataset.id === featuredId) || mediaIds.includes(item.dataset.id);
				reveal(item, show);
				if (show) {
					const img = item.querySelector('img');
					if (img) {
						img.loading = 'eager';
					}
				}
			});
		} else if (mediaIds.length > 0) {
			allItems.forEach(item => {
				reveal(item, mediaIds.includes(item.dataset.id) || (featuredId && item.dataset.id === featuredId));
			});
		} else if (featuredId) {
			// Le variant a une image featured propre mais pas de médias rattachés : ne montrer
			// QUE cette image (même règle que le QAP — uniquement les images de la couleur,
			// jamais celles des autres coloris). Cf. _showImageForVariant dans mm_quick_add.js.
			allItems.forEach(item => {
				reveal(item, item.dataset.id === featuredId);
			});
		} else {
			// Variant sans aucune image propre : fallback sur les premières images du produit.
			allItems.forEach((item, i) => {
				reveal(item, i < MEDIA_CONFIG.defaultMaxImages);
			});
		}

		// Message "pas d'aperçu" : affiché quand la couleur sélectionnée n'a aucune image propre
		// (ni média rattaché, ni featured), masqué sinon. Aligne le rendu après sélection sur le hover.
		const hasOwnImage = extraCount > 0 || mediaIds.length > 0 || !!featuredId;
		// Toggle desktop + mobile : seul le message du conteneur média visible s'affiche.
		document.querySelectorAll('.mm-pdp-no-preview')
			.forEach(el => el.classList.toggle('mm-pdp-no-preview--visible', !hasOwnImage));

		// L'image featured du variant remonte en 1ère position visuelle du flex column.
		if (featuredId) {
			const featuredItem = desktopSlider.querySelector(`[data-id="${featuredId}"]`);
			if (featuredItem && !featuredItem.classList.contains('is-hidden-variant')) {
				featuredItem.style.order = '-1';
			}
		}

		// Afficher les extra metaobject du variant courant, cacher les autres
		const extraContainer = document.querySelector('[data-extra-slides-desktop]');
		if (extraContainer) {
			extraContainer.querySelectorAll('[data-id]').forEach(item => {
				const id = item.dataset.id || '';
				const show = !!(variantId && id.startsWith(`meta-${variantId}-`));
				const wasHidden = item.style.display === 'none';
				item.style.display = show ? '' : 'none';
				if (show && wasHidden) this._replayFadeIn(item);
			});
		}

		// Renuméroter data-index selon l'ordre visuel réel (slider principal + extras).
		// Le flex column respecte `order` : la featured (order:-1) passe devant. On trie donc
		// les items visibles par order effectif avant de réindexer, sinon data-index (et le
		// badge info_modele) suivrait l'ordre DOM et non l'ordre affiché.
		let visualIndex = 0;
		let secondVisibleItem = null;
		const visibleMain = Array.prototype.filter.call(allItems, item => !item.classList.contains('is-hidden-variant'));
		visibleMain.sort((a, b) => (parseInt(a.style.order, 10) || 0) - (parseInt(b.style.order, 10) || 0));
		visibleMain.forEach(item => {
			item.dataset.index = visualIndex;
			if (visualIndex === 1) secondVisibleItem = item;
			visualIndex++;
		});
		if (extraContainer) {
			extraContainer.querySelectorAll('[data-id]').forEach(item => {
				if (item.style.display !== 'none') {
					item.dataset.index = visualIndex;
					if (visualIndex === 1) secondVisibleItem = item;
					visualIndex++;
				}
			});
		}

		// Déplacer le badge info_modele sur le 2ème item visible
		const infoModele = desktopSlider.querySelector('.mm-media-info-modele')
			|| extraContainer?.querySelector('.mm-media-info-modele');
		if (infoModele && secondVisibleItem && !secondVisibleItem.contains(infoModele)) {
			secondVisibleItem.appendChild(infoModele);
		}
	},

	/**
	 * Reveal the matching desktop media item, hide the previously revealed one if it was beyond position 4,
	 * and scroll to it.
	 */
	_scrollDesktopToMedia(targetId) {
		const desktopSlider = document.querySelector(MEDIA_CONFIG.selectors.mainSlider);
		if (!desktopSlider) return;

		const items = Array.from(desktopSlider.querySelectorAll('.mm-pdp-media-item'));

		// Re-hide previously revealed variant item (beyond position 4)
		desktopSlider.querySelectorAll('.is-active-variant').forEach(el => {
			el.classList.remove('is-active-variant');
			const idx = items.indexOf(el);
			if (idx >= MEDIA_CONFIG.defaultMaxImages) {
				el.classList.add('is-hidden-variant');
			}
		});

		const targetItem = desktopSlider.querySelector(`[data-id="${targetId}"]`);
		if (!targetItem) return;

		// Reveal if hidden
		targetItem.classList.remove('is-hidden-variant');
		targetItem.classList.add('is-active-variant');
		targetItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
	},

	/**
	 * Hide desktop media items beyond the first defaultMaxImages on page load.
	 */
	initDefaultDisplay() {
		const desktopSlider = document.querySelector(MEDIA_CONFIG.selectors.mainSlider);
		if (!desktopSlider) return;
		const items = desktopSlider.querySelectorAll('.mm-pdp-media-item');
		items.forEach((item, i) => {
			if (i >= MEDIA_CONFIG.defaultMaxImages) {
				item.classList.add('is-hidden-variant');
			}
		});
	},

	/**
	 * Hide desktop variant media items beyond the first defaultMaxImages on page load.
	 */
	initVariantDefaultDisplay() {
		const desktopVariantSlider = document.querySelector(MEDIA_CONFIG.selectors.mainSliderVariant);
		if (!desktopVariantSlider) return;
		const items = desktopVariantSlider.querySelectorAll('.mm-pdp-media-item');
		items.forEach((item, i) => {
			if (i >= MEDIA_CONFIG.variantMaxImages) {
				item.style.display = 'none';
			}
		});
	},

	/**
	 * Read variant media info from the Liquid-injected script tag.
	 * Returns { mediaIds: string[], featuredId: string|null, extraCount: number, extra: object[] }
	 */
	getVariantMediaInfo(variantId) {
		try {
			const script = document.querySelector(MEDIA_CONFIG.selectors.variantMediaIds);
			if (!script) return { mediaIds: [], featuredId: null, extraCount: 0, extra: [] };
			const map = JSON.parse(script.textContent);
			const entry = map[variantId];
			if (!entry) return { mediaIds: [], featuredId: null, extraCount: 0, extra: [] };
			const mediaIds = (entry.media || []).map(String);
			const featuredId = entry.featured ? String(entry.featured) : null;
			const extra = Array.isArray(entry.extra) ? entry.extra : [];
			const extraCount = entry.extraCount || extra.length || 0;
			return { mediaIds, featuredId, extraCount, extra };
		} catch {
			return { mediaIds: [], featuredId: null, extraCount: 0, extra: [] };
		}
	},

	/**
	 * Ensure the extra metaobject images of a variant exist in [data-extra-slides-desktop].
	 * Only the initially-selected variant is server-rendered ; others are injected on demand
	 * (au clic d'une couleur) depuis le JSON #variant-media-ids. Idempotent : once injected,
	 * the items stay in the DOM. Desktop stack + mobile slider both read this container.
	 */
	_ensureExtraDesktopItems(variantId) {
		const container = document.querySelector('[data-extra-slides-desktop]');
		if (!container || !variantId) return;

		// Already present (server-rendered initial variant, or previously injected)
		if (container.querySelector(`[data-id^="meta-${variantId}-"]`)) return;

		const { extra } = this.getVariantMediaInfo(String(variantId));
		if (!extra.length) return;

		const frag = document.createDocumentFragment();
		extra.forEach((img, index) => {
			if (!img || !img.src) return;
			const item = document.createElement('div');
			item.className = 'mm-pdp-media-item';
			item.setAttribute('data-id', img.id || `meta-${variantId}-${index}`);
			item.setAttribute('data-index', String(index));
			item.style.display = 'none';

			const el = document.createElement('img');
			el.className = 'mm-main-product-media-elem mm-w-full';
			el.src = this.getShopifyImageUrl(img.src, MEDIA_CONFIG.imageWidths.main);
			el.srcset = this.generateResponsiveSrcset(img.src);
			el.sizes = '(min-width: 750px) calc(100vw - 22rem), 1000px';
			el.alt = img.alt || '';
			el.loading = 'lazy';
			el.decoding = 'async';
			if (img.width) el.width = img.width;
			if (img.height) el.height = img.height;

			item.appendChild(el);
			frag.appendChild(item);
		});
		container.appendChild(frag);
	},

	/**
	 * Update slider content with new variant media
	 */
	updateSliderContent(variantId, variantMedia) {
		const mainSlider = document.querySelector(MEDIA_CONFIG.selectors.mainSliderVariant);
		const thumbSlider = document.querySelector(MEDIA_CONFIG.selectors.thumbnailSliderVariant);

		if (!mainSlider || !thumbSlider) {
			window.log('error', 'MediaManager', 'Variant sliders not found');
			return;
		}

		// Update data-variantId attributes
		mainSlider.setAttribute('data-variantId', variantId);
		thumbSlider.setAttribute('data-variantId', variantId);

		// Update existing images instead of regenerating HTML
		this.updateExistingImages(mainSlider, thumbSlider, variantMedia);

		// Update desktop variant stack
		this._updateDesktopVariantStack(variantMedia);

		// Refresh Splide to handle potential slide count changes
		this.refreshSliders(variantMedia.length);
	},

	/**
	 * Update the desktop variant image stack with the given media list (max defaultMaxImages shown).
	 */
	_updateDesktopVariantStack(variantMedia) {
		const desktopSlider = document.querySelector(MEDIA_CONFIG.selectors.mainSliderVariant);
		if (!desktopSlider) return;
		const items = Array.from(desktopSlider.querySelectorAll('.mm-pdp-media-item'));
		const max = MEDIA_CONFIG.variantMaxImages;

		variantMedia.slice(0, max).forEach((media, i) => {
			const mediaData = this.extractMediaData(media);
			if (!mediaData.url) return;
			const item = items[i];
			if (!item) return;
			const img = item.querySelector('img');
			if (!img) return;
			img.src = this.getShopifyImageUrl(mediaData.url, MEDIA_CONFIG.imageWidths.main);
			img.alt = mediaData.alt || '';
			item.setAttribute('data-id', mediaData.id);
			item.style.display = '';
		});

		// Hide items beyond the media count or beyond max
		items.forEach((item, i) => {
			if (i >= Math.min(variantMedia.length, max)) {
				item.style.display = 'none';
			}
		});
	},

	/**
	 * Update existing images or add/remove slides as needed
	 */
	updateExistingImages(mainSlider, thumbSlider, variantMedia) {
		const slides = {
			main: mainSlider.querySelectorAll(MEDIA_CONFIG.selectors.splideSlide),
			thumb: thumbSlider.querySelectorAll(MEDIA_CONFIG.selectors.splideSlide)
		};
		
		const configs = [
			{ slides: slides.main, slider: mainSlider, width: MEDIA_CONFIG.imageWidths.main, className: MEDIA_CONFIG.classes.mainProductMediaElem },
			{ slides: slides.thumb, slider: thumbSlider, width: MEDIA_CONFIG.imageWidths.thumbnail, className: MEDIA_CONFIG.classes.thumbnailMedia }
		];
		
		// Update or add slides for each slider
		configs.forEach(({ slides, slider, width, className }) => {
			variantMedia.forEach((media, index) => {
				if (slides[index]) {
					this.updateSlideImage(slides[index], media, width, className, index);
				} else {
					this.addNewSlide(slider, media, width, className, index);
				}
			});
			
			// Remove extra slides if variant has fewer images
			this.removeExtraSlides(slider, variantMedia.length);
		});
	},

	/**
	 * Update an existing slide's image
	 */
	updateSlideImage(slide, media, width, className, index) {
		const img = slide.querySelector(MEDIA_CONFIG.selectors.slideImage);
		if (!img || !media) {
			window.log('error', 'MediaManager', 'Invalid media or image element', { hasImg: !!img, media });
			return;
		}

		try {
			// Extract media properties based on type
			const mediaData = this.extractMediaData(media);
			if (!mediaData.url) return;

			// Update image attributes
			const imageUrl = this.getShopifyImageUrl(mediaData.url, width);
			img.src = imageUrl;
			img.alt = mediaData.alt;
			img.className = className;

			// Update responsive srcset
			if (img.srcset) {
				img.srcset = this.generateResponsiveSrcset(mediaData.url);
			}

			// Update responsive dimensions if available
			if (mediaData.width && mediaData.height) {
				img.setAttribute('width', mediaData.width);
				img.setAttribute('height', mediaData.height);
			}

			// Update slide navigation attributes
			slide.setAttribute('data-id', mediaData.id);
			slide.setAttribute('data-index', index);

			// Sync overlays visibility based on slide position
			if (className === MEDIA_CONFIG.classes.mainProductMediaElem) {
				this._syncOverlays(slide, index);
			}
		} catch (error) {
			window.log('error', 'MediaManager', 'Failed to update slide image', { media, width, className, index, error: error.message });
		}
	},

	/**
	 * Read the info_modele map injected by Liquid : { default: string, variants: { id: string } }.
	 * `variants` ne contient que les variants ayant leur propre metafield ; les autres retombent
	 * sur `default` (metafield produit). Returns null si absent (aucun variant ne porte le metafield).
	 */
	getVariantInfoModele() {
		if (this._infoModeleMap !== undefined) return this._infoModeleMap;
		try {
			const script = document.querySelector(MEDIA_CONFIG.selectors.variantInfoModele);
			this._infoModeleMap = script ? JSON.parse(script.textContent) : null;
		} catch {
			this._infoModeleMap = null;
		}
		return this._infoModeleMap;
	},

	/**
	 * Rewrite the info_modele overlay text for the given variant (info modèle par couleur).
	 * No-op si aucune map (pas de metafield variant). L'overlay est toujours rendu en SSR
	 * (vide si pas d'info) — ici on ne touche qu'à son texte (HTML <br />) et sa visibilité.
	 */
	_updateInfoModele(variantId) {
		const map = this.getVariantInfoModele();
		if (!map) return;

		const html = (map.variants && map.variants[variantId]) || map.default || '';
		document.querySelectorAll(MEDIA_CONFIG.selectors.infoModele).forEach(overlay => {
			overlay.innerHTML = html;
			overlay.hidden = !html;
		});
	},

	/**
	 * HTML de l'overlay info_modele pour le variant courant, à poser sur la 2e slide mobile
	 * uniquement (index 1) — même règle que le desktop, qui déplace le badge sur le 2e item
	 * visible. Renvoie '' pour toute autre position ou si le variant n'a pas d'info modèle.
	 * Le texte vient TOUJOURS de la map (jamais d'un clone DOM figé sur le variant SSR).
	 */
	_infoModeleSlideHtml(variantId, index) {
		if (index !== 1) return '';
		const map = this.getVariantInfoModele();
		// Pas de map (aucun metafield variant) : le texte est celui du produit, identique pour
		// toutes les couleurs. On le mémorise depuis le SSR avant tout swap de slides.
		if (!map) {
			if (this._productInfoModeleHtml === undefined) {
				const ssr = document.querySelector(MEDIA_CONFIG.selectors.infoModele);
				this._productInfoModeleHtml = ssr && !ssr.hidden ? ssr.innerHTML : '';
			}
			return this._productInfoModeleHtml
				? `<div class="mm-media-info-modele text-sm--paragraph mm-black-text">${this._productInfoModeleHtml}</div>`
				: '';
		}
		const html = (map.variants && map.variants[variantId]) || map.default || '';
		if (!html) return '';
		return `<div class="mm-media-info-modele text-sm--paragraph mm-black-text">${html}</div>`;
	},

	/**
	 * Show/hide info_modele overlay based on slide position (positions 1 and 3).
	 * Tag and rating overlays are on the container — not managed here.
	 */
	_syncOverlays(slide, index) {
		const info = slide.querySelector('.mm-media-info-modele');
		if (info) info.style.display = (index === 1 || index === 3) ? '' : 'none';
	},

	/**
	 * Add a new slide (clone existing structure)
	 */
	addNewSlide(slider, media, width, className, index) {
		const existingSlide = slider.querySelector(MEDIA_CONFIG.selectors.splideSlide);
		if (!existingSlide) return;

		const newSlide = existingSlide.cloneNode(true);
		newSlide.removeAttribute('data-id');
		newSlide.removeAttribute('data-index');

		// Remove tag/rating overlays cloned from slide 0 — they live on the container now
		newSlide.querySelector('.mm-media-tag-overlay')?.remove();
		newSlide.querySelector('.mm-media-rating-overlay')?.remove();

		// Inject info_modele on positions 1 and 3 if present on any original slide
		if (index === 1 || index === 3) {
			const sourceSlide = slider.querySelectorAll(MEDIA_CONFIG.selectors.splideSlide)[index];
			const existingInfo = sourceSlide?.querySelector('.mm-media-info-modele');
			if (!newSlide.querySelector('.mm-media-info-modele') && existingInfo) {
				newSlide.appendChild(existingInfo.cloneNode(true));
			}
		}

		this.updateSlideImage(newSlide, media, width, className, index);
		slider.querySelector(MEDIA_CONFIG.selectors.splideList).appendChild(newSlide);
	},

	/**
	 * Remove extra slides when variant has fewer images
	 */
	removeExtraSlides(slider, keepCount) {
		const slides = slider.querySelectorAll(MEDIA_CONFIG.selectors.splideSlide);
		for (let i = slides.length - 1; i >= keepCount; i--) {
			slides[i].remove();
		}
	},

	/**
	 * Refresh Splide sliders after content changes
	 */
	refreshSliders(newSlideCount) {
		const sliderId = 'variant';
		const sliderPair = window.ProductMediaSliders?.instances?.[sliderId];
		
		if (sliderPair?.main?.refresh) {
			try {
				// Refresh instead of destroying/recreating
				sliderPair.main?.refresh();
				sliderPair.thumbnail?.refresh();
				mediaEmit('variant-media-splide-refresh', { sliderId, slideCount: newSlideCount });
			} catch (error) {
				// Fallback: destroy and recreate if refresh fails
				window.log('warn', 'MediaManager', 'Splide refresh failed, recreating', { error: error.message });
				this.reloadSliders();
			}
		}
	},

	/**
	 * Fallback: Reload Splide sliders completely
	 */
	reloadSliders() {
		const sliderId = 'variant';
		mediaEmit('variant-media-splide-reload', { sliderId });
		
		if (window.ProductMediaSliders?.destroy && window.ProductMediaSliders?.init) {
			window.ProductMediaSliders.destroy(sliderId);
			window.ProductMediaSliders.init(sliderId, true);
		} else {
			window.log('warn', 'MediaManager', 'ProductMediaSliders methods not available for reload', {
				sliderExists: !!window.ProductMediaSliders,
				hasDestroy: !!window.ProductMediaSliders?.destroy,
				hasInit: !!window.ProductMediaSliders?.init
			});
		}
	},

	/**
	 * Extract media data from different media formats
	 */
	extractMediaData(media) {
		if (typeof media === 'string') {
			return {
				url: media,
				alt: '',
				id: this.generateMediaId(media),
				width: null,
				height: null
			};
		}
		
		if (media.src) {
			return {
				url: media.src,
				alt: media.alt || '',
				id: media.id || this.generateMediaId(media.src),
				width: media.width,
				height: media.height
			};
		}
		
		if (media.preview_image) {
			return {
				url: media.preview_image.src,
				alt: media.alt || '',
				id: media.id || this.generateMediaId(media.preview_image.src),
				width: media.preview_image.width,
				height: media.preview_image.height
			};
		}
		
		window.log('error', 'MediaManager', 'Invalid media format', { media });
		return { url: null };
	},

	/**
	 * Generate a consistent media ID from URL string
	 */
	generateMediaId(url) {
		// Simple hash function to generate consistent ID from URL
		let hash = 0;
		for (let i = 0; i < url.length; i++) {
			const char = url.charCodeAt(i);
			hash = ((hash << 5) - hash) + char;
			hash = hash & hash; // Convert to 32-bit integer
		}
		return Math.abs(hash).toString();
	},

	/**
	 * Generate responsive srcset for image_tag compatibility
	 */
	generateResponsiveSrcset(originalUrl) {
		if (!originalUrl || typeof originalUrl !== 'string') {
			return '';
		}
		
		// Common responsive breakpoints used by Shopify image_tag
		const breakpoints = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1600, 1800, 2000];
		
		return breakpoints
			.map(width => `${this.getShopifyImageUrl(originalUrl, width)} ${width}w`)
			.join(', ');
	},

	/**
	 * Generate Shopify image URL with specified width
	 */
	getShopifyImageUrl(originalUrl, width) {
		if (!originalUrl || typeof originalUrl !== 'string') {
			return originalUrl;
		}
		
		try {
			// Remove any existing size parameters
			let cleanUrl = originalUrl.replace(/_\d+x\d*\./g, '.');
			
			// Handle URLs with query parameters (like v= version)
			const [baseUrl, queryString] = cleanUrl.split('?');
			
			// Add width parameter before file extension
			const sizedUrl = baseUrl.replace(/(\.[^.]*$)/, `_${width}x$1`);
			
			// Reattach query string if it exists
			return queryString ? `${sizedUrl}?${queryString}` : sizedUrl;
		} catch (error) {
			window.log('warn', 'MediaManager', 'Failed to resize image URL, using original', { originalUrl, error: error.message });
			return originalUrl;
		}
	}
};

/*********************************************************************************************
 * 🌗 CONTRASTE DES FLÈCHES MOBILE
 * Les chevrons sont gris clair (lisibles sur photo) mais disparaissent sur un fond blanc.
 * À chaque slide, on mesure la luminosité de l'image SOUS chaque flèche et on passe la flèche
 * en noir (.mm-arrow--dark) si le fond est clair. Même chose pour la pagination (barres) : sur
 * fond clair, la barre active passe en noir (.mm-pagination--dark). Lecture via une miniature 100px chargée en
 * CORS (le CDN Shopify renvoie Access-Control-Allow-Origin: *) : l'<img> affichée, chargée
 * sans crossOrigin, rendrait le canvas illisible. Résultat mis en cache par image.
 */
const ArrowContrast = {
	sampleWidth: 100,
	// Luminance moyenne (0-255) au-delà de laquelle le gris clair ne se voit plus → flèche noire
	threshold: 190,
	cache: new Map(),

	/** extras : éléments hors du slider à traiter comme les flèches (ex. croix du zoom mobile). */
	bind(splide, extras = []) {
		if (!splide?.root) return;
		splide._contrastExtras = extras;
		const update = () => this.update(splide);
		// refresh : la pagination est régénérée par Splide (slider variant) → reposer la classe
		splide.on('mounted move resized refresh', update);
		update();
	},

	async update(splide) {
		if (!window.isMobile()) return;
		const slide = splide.Components.Slides.getAt(splide.index)?.slide;
		const img = slide?.querySelector('img');
		const arrows = [...splide.root.querySelectorAll('.splide__arrow'), ...(splide._contrastExtras || [])];
		if (!img) return;

		const pagination = splide.root.querySelector('.splide__pagination');
		const targets = arrows.map(arrow => [arrow, 'mm-arrow--dark']);
		if (pagination) targets.push([pagination, 'mm-pagination--dark']);
		// La slide a pu changer pendant le chargement de la miniature
		await this.apply(img, targets, {
			isCurrent: () => splide.Components.Slides.getAt(splide.index)?.slide === slide
		});
	},

	/**
	 * Pose chaque classe [el, className] si l'image est claire sous l'élément.
	 * Repère = l'image elle-même, pas son conteneur : dans le zoom elle peut être entourée
	 * de bandes ou déborder. outsideLight = ce qu'on considère hors de l'image (fond #f6f6f6
	 * des slides → clair ; backdrop sombre du zoom desktop → foncé).
	 */
	async apply(img, targets, { outsideLight = true, isCurrent = () => true } = {}) {
		const pixels = await this._pixels(img);
		if (!pixels || !isCurrent()) return;

		const box = img.getBoundingClientRect();
		if (!box.width || !box.height) return;
		const isLightUnder = (el) => {
			const r = el.getBoundingClientRect();
			if (!r.width || !r.height) return false;
			const x0 = (r.left - box.left) / box.width, x1 = (r.right - box.left) / box.width;
			const y0 = (r.top - box.top) / box.height, y1 = (r.bottom - box.top) / box.height;
			if (x1 <= 0 || x0 >= 1 || y1 <= 0 || y0 >= 1) return outsideLight;
			const lum = this._luminance(pixels, x0, y0, x1, y1);
			return lum !== null && lum > this.threshold;
		};
		targets.forEach(([el, className]) => el.classList.toggle(className, isLightUnder(el)));
	},

	/* Pixels de la miniature (ImageData) — promesse mise en cache par URL d'image. */
	_pixels(img) {
		const src = img.currentSrc || img.getAttribute('src');
		if (!src) return Promise.resolve(null);
		let url;
		try {
			url = new URL(src, window.location.href);
		} catch {
			return Promise.resolve(null);
		}
		url.searchParams.set('width', this.sampleWidth);
		const key = url.toString();
		if (this.cache.has(key)) return this.cache.get(key);

		const promise = new Promise(resolve => {
			const sample = new Image();
			sample.crossOrigin = 'anonymous';
			sample.onload = () => {
				try {
					const w = this.sampleWidth;
					const h = Math.max(1, Math.round(w * sample.naturalHeight / sample.naturalWidth));
					const canvas = document.createElement('canvas');
					canvas.width = w;
					canvas.height = h;
					const ctx = canvas.getContext('2d', { willReadFrequently: true });
					ctx.drawImage(sample, 0, 0, w, h);
					resolve(ctx.getImageData(0, 0, w, h));
				} catch {
					resolve(null); // canvas tainted (CORS) → on garde la couleur par défaut
				}
			};
			sample.onerror = () => resolve(null);
			sample.src = key;
		});
		this.cache.set(key, promise);
		return promise;
	},

	/* Luminance moyenne d'une zone exprimée en fractions (0-1) de l'image. */
	_luminance(pixels, x0, y0, x1, y1) {
		const { width, height, data } = pixels;
		const clamp = (v, max) => Math.min(max, Math.max(0, Math.round(v)));
		const left = clamp(x0 * width, width - 1), right = clamp(x1 * width, width);
		const top = clamp(y0 * height, height - 1), bottom = clamp(y1 * height, height);
		let sum = 0, count = 0;
		for (let y = top; y < bottom; y++) {
			for (let x = left; x < right; x++) {
				const i = (y * width + x) * 4;
				sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
				count++;
			}
		}
		return count ? sum / count : null;
	}
};

/*********************************************************************************************
 * 🎠 PRODUCT MEDIA SLIDERS (Splide)
 */
const ProductMediaSliders = {
	config: {
		main: {
			type: 'slide',
			rewind: true,
			autoHeight: true,
			pagination: true,
			arrows: true
		},
		thumbnail: {
			type: 'slide',
			perPage: 6,
			gap: 8,
			height: '100%',
			width: 90,
			direction: 'ttb',
			isNavigation: true,
			pagination: false,
			arrows: false,
			breakpoints: {
				768: {
					arrows: true,
					direction: 'ltr',
					width: false,
					heightRatio: 1,
					isNavigation: false,
					perPage: 1
				}
			}
		}
	},

	instances: {},

	init(sliderId, isVariant = false) {
		if (this.instances[sliderId]) return this.instances[sliderId];

		const isMobile = window.isMobile();
		const mainEl = document.querySelector(isVariant ? `#main-slider-${sliderId}` : '#main-slider');
		if (!mainEl) return null;

		try {
			const mainSlider = new Splide(mainEl, this.config.main);

			if (!isMobile) {
				const thumbEl = document.querySelector(isVariant ? `#thumbnail-slider-${sliderId}` : '#thumbnail-slider');
				if (thumbEl) {
					const thumbSlider = new Splide(thumbEl, this.config.thumbnail);
					mainSlider.sync(thumbSlider);
					mainSlider.mount();
					thumbSlider.mount();
					this.instances[sliderId] = { main: mainSlider, thumbnail: thumbSlider };
				} else {
					mainSlider.mount();
					this.instances[sliderId] = { main: mainSlider };
				}
			} else {
				mainSlider.mount();
				ArrowContrast.bind(mainSlider);
				this.instances[sliderId] = { main: mainSlider };

				// Splide can't compute slide dimensions when the wrapper is display:none.
				// Watch for the element becoming visible and refresh once it has width.
				if (mainEl.offsetWidth === 0) {
					const ro = new ResizeObserver(() => {
						if (mainEl.offsetWidth > 0) {
							ro.disconnect();
							mainSlider.refresh();
						}
					});
					ro.observe(mainEl);
				}
			}

			return this.instances[sliderId];
		} catch (error) {
			window.log('error', 'ProductMediaSliders', 'Slider initialization failed', { error: error.message });
			return null;
		}
	},

	destroy(sliderId) {
		const pair = this.instances[sliderId];
		if (pair) {
			try {
				pair.main?.destroy();
				pair.thumbnail?.destroy();
			} finally {
				delete this.instances[sliderId];
			}
		}
	},

	destroyAll() {
		Object.keys(this.instances).forEach(id => this.destroy(id));
	}
};

/*********************************************************************************************
 * 🔍 ZOOM PLEIN ÉCRAN (lightbox)
 *
 * Ouvre l'image cliquée en plein écran, agrandie et parcourable au scroll/drag, avec
 * navigation entre les images du coloris courant.
 *
 * Points d'attention :
 * - La liste des images est TOUJOURS recalculée à l'ouverture depuis le DOM visible
 *   (desktop : items non masqués par _applyDesktopFilter ; mobile : slides du Splide courant).
 *   Le filtrage couleur est donc respecté sans dupliquer sa logique ici.
 * - Le Splide mobile est remonté à chaque changement de couleur (_remountMobileSlider) :
 *   les listeners sont délégués sur document, jamais posés sur les slides.
 * - Lenis doit être stoppé à l'ouverture, sinon le smooth scroll continue derrière l'overlay.
 */
const ZoomManager = {
	// L'image occupe 100% de la largeur du viewport ; comme elle est en portrait, elle
	// dépasse en hauteur → le scroll vertical la parcourt (même principe que la référence).
	// Largeur max demandée au CDN Shopify (borne les téléchargements). 3000 = largeur des
	// masters produit : au-delà le CDN renverrait de toute façon l'original.
	maxImageWidth: 3000,

	root: null,
	viewport: null,
	img: null,
	cursor: null,

	items: [],
	index: 0,
	thumbs: null,
	sliderEl: null,
	slider: null,
	isOpen: false,
	lastFocused: null,

	init() {
		this.root = document.querySelector('[data-media-zoom]');
		if (!this.root) return;

		this.viewport = this.root.querySelector('[data-media-zoom-viewport]');
		this.img = this.root.querySelector('[data-media-zoom-img]');
		this.thumbs = this.root.querySelector('[data-media-zoom-thumbs]');
		this.sliderEl = this.root.querySelector('[data-media-zoom-slider]');
		this.cursor = document.querySelector('[data-media-zoom-cursor]');

		if (!this.viewport || !this.img) return;

		// Un seul bind, même si init() est rejoué (transition Barba).
		if (!this._bound) {
			this._bindEvents();
			this._bound = true;
		}
	},

	/**
	 * Médias cliquables actuellement visibles. Desktop = stack (items non filtrés),
	 * mobile = slides du Splide. On lit le DOM à chaque ouverture : c'est la seule
	 * source de vérité après un filtrage couleur ou un remount.
	 */
	_visibleMedias() {
		const isMobile = window.matchMedia('(width <= 768px)').matches;

		const nodes = isMobile
			? document.querySelectorAll('[data-main-slider-mobile] [data-splide-slide]:not(.splide__slide--clone)')
			: document.querySelectorAll('[data-main-slider]:not([data-variant-slider]) .mm-pdp-media-item, [data-main-slider][data-variant-slider] .mm-pdp-media-item, [data-extra-slides-desktop] .mm-pdp-media-item');

		return Array.from(nodes).filter(node => {
			if (node.classList.contains('is-hidden-variant')) return false;
			if (node.style.display === 'none') return false;
			// offsetParent null = ancêtre masqué (stack desktop sous 768px, et inversement)
			if (node.offsetParent === null) return false;
			return !!node.querySelector('img');
		});
	},

	/**
	 * URL haute résolution : on repart de l'URL du CDN sans paramètre de taille,
	 * puis on demande la largeur voulue via ?width= (format moderne, contrairement
	 * au _1000x legacy de getShopifyImageUrl).
	 */
	_highResUrl(img) {
		// Largeur d'AFFICHAGE réelle, pas la largeur du viewport : sur mobile l'image est
		// dimensionnée par la hauteur (min-height:100%) et déborde donc latéralement — se
		// baser sur innerWidth demandait 3× moins de pixels que nécessaire (image floue).
		// Mesurée sur l'élément du zoom s'il est déjà dimensionné, sinon estimée depuis le
		// ratio du média et la hauteur du viewport (1er rendu, avant tout layout).
		const rendered = this.img?.getBoundingClientRect().width || 0;
		let cssWidth = rendered;

		if (!cssWidth) {
			const w = img.naturalWidth || Number(img.getAttribute('width')) || 0;
			const h = img.naturalHeight || Number(img.getAttribute('height')) || 0;
			const byHeight = w && h ? window.innerHeight * (w / h) : 0;
			// Mobile : l'image est calée sur la hauteur et peut dépasser la largeur.
			cssWidth = Math.max(window.innerWidth, byHeight);
		}

		// Pas de cap à 2 sur le DPR : les écrans mobiles à 3x sont la cible principale du zoom.
		const dpr = window.devicePixelRatio || 1;
		const cap = this._masterCap(img);
		const target = Math.round(Math.min(cssWidth * dpr, cap));
		return this._resizedUrl(img, target);
	},

	/**
	 * Largeur maximale utile à demander au CDN : celle du fichier d'origine.
	 * On lit l'attribut `width` du SSR (dimensions du master, ex. 2000) et NON
	 * `naturalWidth`, qui vaut la taille de la variante affichée dans la galerie
	 * (ex. 1088) et brideraient donc le zoom à une résolution plus basse.
	 */
	_masterCap(img) {
		const master = Number(img.getAttribute('width')) || img.naturalWidth || 0;
		return master ? Math.min(this.maxImageWidth, master) : this.maxImageWidth;
	},

	/** Même URL CDN, à la largeur demandée (?width=). */
	_resizedUrl(img, width) {
		// currentSrc = ce que le navigateur a réellement choisi dans le srcset
		const raw = img.currentSrc || img.src || '';
		if (!raw) return '';

		try {
			const url = new URL(raw, window.location.origin);
			url.searchParams.set('width', String(width));
			return url.toString();
		} catch (e) {
			return raw;
		}
	},

	open(mediaNode) {
		if (!this.root) return;

		this.items = this._visibleMedias();
		const start = this.items.indexOf(mediaNode);
		this.index = start === -1 ? 0 : start;
		if (!this.items.length) return;

		this.lastFocused = document.activeElement;
		this.isOpen = true;

		this.root.hidden = false;
		this.root.classList.toggle('is-single', this.items.length <= 1);

		// Mobile : slider plein écran identique au swipe de la galerie (le root doit être
		// visible avant le mount, Splide mesure le track). Desktop : image agrandie + vignettes.
		const useSlider = !!this.sliderEl && window.matchMedia('(width <= 768px)').matches;
		this.root.classList.toggle('is-slider', useSlider);
		if (useSlider) {
			this._mountSlider();
		} else {
			this._buildThumbs();
			this._render();
		}

		// Le curseur loupe n'a plus lieu d'être une fois l'overlay ouvert
		this._hideCursor();

		// overflow:hidden ne suffit pas : Lenis pilote le scroll en JS
		window.LenisManager?.stop?.();
		document.body.style.overflow = 'hidden';

		// Laisse le temps au navigateur d'appliquer hidden=false avant la transition d'opacité
		requestAnimationFrame(() => this.root.classList.add('is-open'));

		this.root.querySelector('button[data-media-zoom-close]')?.focus?.({ preventScroll: true });

		mediaEmit('zoom-open', { index: this.index, total: this.items.length });
	},

	close() {
		if (!this.root || !this.isOpen) return;

		this.isOpen = false;
		this.root.classList.remove('is-open');

		document.body.style.overflow = '';
		window.LenisManager?.resume?.();

		// La galerie reprend sur l'image consultée dans le zoom
		if (this.slider) this._syncGallery(this.slider.index);

		const finish = () => {
			if (this.isOpen) return; // rouvert entre-temps
			this.root.hidden = true;
			this.img.removeAttribute('src');
			this._destroySlider();
		};
		// Attendre la fin du fondu (durée alignée sur --duration-ultrashort)
		setTimeout(finish, 300);

		this.lastFocused?.focus?.({ preventScroll: true });
		mediaEmit('zoom-close', {});
	},

	go(delta) {
		if (!this.items.length) return;
		if (this.slider) {
			this.slider.go(delta > 0 ? '>' : '<');
			return;
		}
		const total = this.items.length;
		this.index = (this.index + delta + total) % total;
		this._render();
		mediaEmit('zoom-navigate', { index: this.index, total });
	},

	_render() {
		const node = this.items[this.index];
		const source = node?.querySelector('img');
		if (!source) return;

		this.img.src = this._highResUrl(source);
		this.img.alt = source.alt || '';


		this._syncThumbs();

		// Recentrer la zone de scroll sur la nouvelle image (horizontalement ET verticalement) :
		// l'image dépasse du viewport, sans ça on ouvrirait sur son bord supérieur (la tête du
		// modèle plutôt que le vêtement). Fait après le chargement, quand la hauteur est connue.
		// Centrage : vertical toujours (l'image dépasse en hauteur), horizontal seulement
		// sur mobile où l'image est dimensionnée par la hauteur et déborde latéralement.
		const center = () => {
			// Recentrage programmatique : il emet un evenement scroll, que le listener plus bas
			// interpreterait comme "l'utilisateur parcourt l'image" (_didDrag) — un tap juste
			// apres l'ouverture ne refermerait alors plus le zoom. On le marque pour l'ignorer.
			this._programmaticScroll = true;
			this.viewport.scrollTop = (this.viewport.scrollHeight - this.viewport.clientHeight) / 2;
			this.viewport.scrollLeft = (this.viewport.scrollWidth - this.viewport.clientWidth) / 2;
			requestAnimationFrame(() => { this._programmaticScroll = false; });
		};
		if (this.img.complete) {
			requestAnimationFrame(() => { center(); this._refineResolution(source); this._updateContrast(); });
		} else {
			this.img.addEventListener('load', () => requestAnimationFrame(() => {
				center();
				this._refineResolution(source);
				this._updateContrast();
			}), { once: true });
		}
	},

	/**
	 * Desktop : croix et flèches au style des flèches du slider mobile (gris clair, noir
	 * sur zone claire). L'image défile sous les contrôles → recalculé au scroll du viewport.
	 */
	_updateContrast() {
		if (!this.isOpen || this.slider || !this.img.getAttribute('src')) return;
		const targets = [...this.root.querySelectorAll('.mm-media-zoom__close, .mm-media-zoom__nav')]
			.map(el => [el, 'mm-arrow--dark']);
		ArrowContrast.apply(this.img, targets, { outsideLight: false });
	},

	/**
	 * Une fois l'image posée, sa largeur d'affichage réelle est connue (elle dépend du
	 * ratio du média et de la hauteur du viewport sur mobile). Si elle réclame plus de
	 * pixels que ce qui a été chargé, on redemande la bonne taille au CDN.
	 */
	_refineResolution(source) {
		if (!this.isOpen || !source) return;

		const cap = this._masterCap(source);
		const needed = Math.round(
			Math.min(this.img.getBoundingClientRect().width * (window.devicePixelRatio || 1), cap)
		);
		// Marge de 10% : ne pas relancer un téléchargement pour un écart négligeable.
		if (!needed || this.img.naturalWidth >= needed * 0.9) return;

		const better = this._resizedUrl(source, needed);
		if (better && better !== this.img.src) this.img.src = better;
	},

	/* ── Slider plein écran (mobile) ── */

	/**
	 * Construit une slide par média visible et monte un Splide avec la config de la galerie
	 * (rewind, flèches) + ArrowContrast, ouvert sur l'image tapée. Navigation par les
	 * vignettes images (_buildThumbs) à la place de la pagination en barres.
	 * Images en lazy 'nearby' : seules la courante et ses voisines sont téléchargées.
	 */
	_mountSlider() {
		this._destroySlider();

		const list = this.sliderEl.querySelector('.splide__list');
		if (!list) return;

		const dpr = window.devicePixelRatio || 1;
		list.innerHTML = '';
		this.items.forEach(node => {
			const source = node.querySelector('img');
			const li = document.createElement('li');
			li.className = 'splide__slide';

			const img = document.createElement('img');
			img.className = 'mm-media-zoom__slide-img';
			img.alt = source?.alt || '';
			img.draggable = false;
			// Dimensions du master : réservent le ratio avant chargement (ArrowContrast mesure l'image)
			['width', 'height'].forEach(attr => {
				const value = source?.getAttribute(attr);
				if (value) img.setAttribute(attr, value);
			});
			if (source) {
				// Image calée sur la hauteur d'écran : largeur affichée = hauteur × ratio du média
				const w = Number(source.getAttribute('width')) || source.naturalWidth || 2;
				const h = Number(source.getAttribute('height')) || source.naturalHeight || 3;
				const cssWidth = Math.max(window.innerWidth, window.innerHeight * w / h);
				const width = Math.round(Math.min(cssWidth * dpr, this._masterCap(source)));
				img.setAttribute('data-splide-lazy', this._resizedUrl(source, width));
			}

			li.appendChild(img);
			list.appendChild(li);
		});

		try {
			this.slider = new Splide(this.sliderEl, {
				...ProductMediaSliders.config.main,
				autoHeight: false,
				pagination: false,
				start: this.index,
				lazyLoad: 'nearby'
			});
			this.slider.on('lazyload:loaded', () => ArrowContrast.update(this.slider));
			this.slider.on('move', (index) => {
				this.index = index;
				this._syncThumbs();
				mediaEmit('zoom-navigate', { index, total: this.items.length });
			});
			this.slider.mount();
			// La croix suit le même contraste que les flèches
			const close = this.root.querySelector('.mm-media-zoom__close');
			ArrowContrast.bind(this.slider, close ? [close] : []);
			this._buildThumbs();
			this._syncThumbs();
		} catch (e) {
			window.log?.('warn', 'ZoomManager', 'Zoom slider mount failed', { error: e.message });
			this.slider = null;
		}
	},

	_destroySlider() {
		if (!this.slider) return;
		try {
			this.slider.destroy();
		} catch (e) { /* ignore */ }
		this.slider = null;
		const list = this.sliderEl?.querySelector('.splide__list');
		if (list) list.innerHTML = '';
	},

	/** Aligne le slider mobile de la galerie sur l'index consulté dans le zoom. */
	_syncGallery(index) {
		const galleryRoot = this.items[0]?.closest('.splide');
		const pair = Object.values(ProductMediaSliders.instances)
			.find(p => p?.main?.root === galleryRoot);
		if (pair?.main && pair.main.index !== index) pair.main.go(index);
	},

	/**
	 * (Re)construit les vignettes à l'ouverture, depuis les mêmes médias visibles que
	 * la lightbox. Reconstruites à chaque open() car la liste change avec le coloris.
	 */
	_buildThumbs() {
		if (!this.thumbs) return;

		this.thumbs.innerHTML = '';
		if (this.items.length <= 1) return;

		const fragment = document.createDocumentFragment();

		this.items.forEach((node, i) => {
			const source = node.querySelector('img');
			if (!source) return;

			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'mm-media-zoom__thumb';
			btn.dataset.zoomThumb = String(i);
			btn.setAttribute('aria-label', `${i + 1} / ${this.items.length}`);

			// Ratio réel du média : la vignette garde le format de l'image (jamais de
			// rognage), même si le produit a des visuels d'un autre format que le 2:3.
			// naturalWidth n'est fiable qu'une fois l'image chargée → fallback sur les
			// attributs width/height du SSR, qui portent les dimensions d'origine.
			const w = source.naturalWidth || Number(source.getAttribute('width'));
			const h = source.naturalHeight || Number(source.getAttribute('height'));
			if (w > 0 && h > 0) {
				btn.style.setProperty('--thumb-ratio', `${w} / ${h}`);
			}

			const thumbImg = document.createElement('img');
			// Vignette légère : on repart de l'URL du média et on demande une petite largeur.
			thumbImg.src = this._resizedUrl(source, 160);
			thumbImg.alt = '';
			thumbImg.loading = 'lazy';
			btn.appendChild(thumbImg);

			fragment.appendChild(btn);
		});

		this.thumbs.appendChild(fragment);
	},

	/**
	 * Marque la vignette active et la centre dans la barre (scroll horizontal quand les
	 * vignettes dépassent la largeur). scrollLeft plutôt que scrollIntoView : ce dernier
	 * fait aussi défiler les ancêtres scrollables (track du slider, viewport du zoom).
	 */
	_syncThumbs() {
		if (!this.thumbs) return;

		this.thumbs.querySelectorAll('[data-zoom-thumb]').forEach(btn => {
			const active = Number(btn.dataset.zoomThumb) === this.index;
			btn.classList.toggle('is-active', active);
			btn.setAttribute('aria-current', active ? 'true' : 'false');
			if (active) {
				const bar = this.thumbs;
				bar.scrollTo({
					left: btn.offsetLeft - (bar.clientWidth - btn.offsetWidth) / 2,
					behavior: this.isOpen ? 'smooth' : 'auto'
				});
			}
		});
	},

	/* ── Curseur loupe (desktop pointeur fin uniquement) ── */

	_canUseCursor() {
		return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
	},

	_showCursor(x, y) {
		if (!this.cursor || !this._canUseCursor() || this.isOpen) return;
		this.cursor.style.transform = `translate(${x - 22}px, ${y - 22}px)`;
		this.cursor.classList.add('is-visible');
	},

	_hideCursor() {
		this.cursor?.classList.remove('is-visible');
	},

	/* ── Événements (tous délégués : le DOM média est re-rendu au changement de couleur) ── */

	_bindEvents() {
		const mediaSelector = '.mm-pdp-media-item, [data-main-slider-mobile] [data-splide-slide]';

		// Ouverture. Sur mobile, un swipe Splide ne doit pas déclencher le zoom : on compare
		// la position entre pointerdown et pointerup (un vrai tap ne bouge quasiment pas).
		let downX = 0, downY = 0, downTime = 0;

		document.addEventListener('pointerdown', (e) => {
			if (this.isOpen) return;
			downX = e.clientX;
			downY = e.clientY;
			downTime = Date.now();
		}, { passive: true });

		document.addEventListener('click', (e) => {
			if (this.isOpen) return;

			const media = e.target.closest(mediaSelector);
			if (!media) return;
			// Ne pas capter les clics sur les overlays interactifs posés sur la galerie
			if (e.target.closest('button, a, .splide__arrow')) return;

			const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
			if (moved > 10 || Date.now() - downTime > 600) return; // swipe ou appui long

			e.preventDefault();
			this.open(media);
		});

		// Fermeture : bouton, croix, fond, et clic sur l'image (cursor: zoom-out)
		this.root.addEventListener('click', (e) => {
			if (e.target.closest('[data-media-zoom-prev]')) { this.go(-1); return; }
			if (e.target.closest('[data-media-zoom-next]')) { this.go(1); return; }
			if (e.target.closest('[data-media-zoom-close]')) { this.close(); return; }
			// Flèches du slider mobile : gérées par Splide, ne referment pas.
			// (Un tap sur l'image referme ; un swipe non — Splide stoppe le click qui suit un drag.)
			if (e.target.closest('.splide__arrows')) return;

			const thumb = e.target.closest('[data-zoom-thumb]');
			if (thumb) {
				this.index = Number(thumb.dataset.zoomThumb) || 0;
				if (this.slider) this.slider.go(this.index);
				else this._render();
				return;
			}
			// La barre de vignettes n'est pas une zone de fermeture
			if (e.target.closest('[data-media-zoom-thumbs]')) return;
			// Un drag pour parcourir l'image ne doit pas refermer
			if (this._didDrag) return;
			this.close();
		});

		// Clavier
		document.addEventListener('keydown', (e) => {
			if (!this.isOpen) return;
			if (e.key === 'Escape') { this.close(); }
			else if (e.key === 'ArrowRight') { this.go(1); }
			else if (e.key === 'ArrowLeft') { this.go(-1); }
		});

		// Drag pour parcourir l'image agrandie
		let dragging = false, startX = 0, startY = 0, startLeft = 0, startTop = 0;

		this.viewport.addEventListener('pointerdown', (e) => {
			dragging = true;
			this._didDrag = false;
			startX = e.clientX;
			startY = e.clientY;
			startLeft = this.viewport.scrollLeft;
			startTop = this.viewport.scrollTop;
			this.root.classList.add('is-dragging');
		});

		this.viewport.addEventListener('pointermove', (e) => {
			if (!dragging) return;
			const dx = e.clientX - startX;
			const dy = e.clientY - startY;
			if (Math.hypot(dx, dy) > 5) this._didDrag = true;
			this.viewport.scrollLeft = startLeft - dx;
			this.viewport.scrollTop = startTop - dy;
		});

		const endDrag = () => {
			dragging = false;
			this.root.classList.remove('is-dragging');
			// Laisser le click (qui suit le pointerup) lire _didDrag avant de le réinitialiser
			setTimeout(() => { this._didDrag = false; }, 0);
		};
		this.viewport.addEventListener('pointerup', endDrag);
		this.viewport.addEventListener('pointercancel', endDrag);
		this.viewport.addEventListener('pointerleave', endDrag);

		// Scroll tactile natif : le navigateur s'approprie le geste, scrolle lui-meme et emet
		// pointercancel SANS jamais delivrer de pointermove. _didDrag restait donc a false et le
		// click de fin de geste refermait le zoom (cursor: zoom-out) en pleine consultation.
		// Le scroll — natif comme pilote par le drag JS — vaut donc aussi "l'utilisateur parcourt
		// l'image", et doit neutraliser la fermeture au click.
		this.viewport.addEventListener('scroll', () => {
			if (!this.isOpen || this._programmaticScroll) return;
			this._didDrag = true;
			if (!this._contrastFrame) {
				this._contrastFrame = requestAnimationFrame(() => {
					this._contrastFrame = null;
					this._updateContrast();
				});
			}
			clearTimeout(this._scrollIdleTimer);
			// Rearme apres la fin du geste, sinon un vrai tap suivant ne fermerait plus.
			this._scrollIdleTimer = setTimeout(() => { this._didDrag = false; }, 150);
		}, { passive: true });

		// Curseur loupe : suit la souris sur les médias de la galerie
		document.addEventListener('pointermove', (e) => {
			if (this.isOpen || !this._canUseCursor()) return;
			if (e.pointerType && e.pointerType !== 'mouse') return;

			const media = e.target.closest(mediaSelector);
			if (media) {
				media.classList.add('mm-has-zoom-cursor');
				this._showCursor(e.clientX, e.clientY);
			} else {
				this._hideCursor();
			}
		}, { passive: true });

		document.addEventListener('pointerleave', () => this._hideCursor(), { passive: true });

		// Redimensionnement : la largeur de l'image dépend du viewport
		window.addEventListener('resize', () => {
			if (this.isOpen && !this.slider) this._render();
		});
	}
};

/*********************************************************************************************
 * 🚀 PAGE INIT
 */
function initProductMediaPage() {
	const isVariant = !!document.querySelector(MEDIA_CONFIG.selectors.mainSliderVariant);

	// Destroy existing Splide instances before reinit (Barba transition)
	ProductMediaSliders.destroyAll();

	if (isVariant) {
		ProductMediaSliders.init('variant', true);
		MediaManager.initVariantDefaultDisplay();
	} else if (document.querySelector(MEDIA_CONFIG.selectors.mainSlider)) {
		ProductMediaSliders.init('main', false);
		MediaManager.initDefaultDisplay();
		// Par défaut on montre TOUTES les images produit ; le filtrage sur la couleur ne se
		// fait qu'au 1er clic d'une pastille (onVariantChange → updateMediaDisplay). On ne
		// filtre à l'arrivée QUE si l'URL force explicitement une couleur (?color= / ?variant=).
		const params = new URLSearchParams(MM_ENTRY_SEARCH);
		const urlForcesColor = params.has('color') || params.has('variant');
		const initialVariantId = document.querySelector('[data-selected-variant-id]')?.value;
		// Aligner l'info modèle sur le variant réellement sélectionné (le SSR rend celui du
		// metafield produit/variant par défaut, qui peut différer de la couleur présélectionnée).
		if (initialVariantId) MediaManager._updateInfoModele(initialVariantId);
		if (urlForcesColor && initialVariantId) {
			MediaManager.userSelectedColor = true; // l'URL = choix explicite → filtrages ultérieurs actifs
			const { mediaIds, featuredId, extraCount } = MediaManager.getVariantMediaInfo(initialVariantId);
			MediaManager._applyDesktopFilter(mediaIds, initialVariantId, extraCount, featuredId);
			requestAnimationFrame(() => {
				MediaManager._applyDesktopFilter(mediaIds, initialVariantId, extraCount, featuredId);
				// Mobile : même filtrage que le desktop dès l'arrivée (sinon le swipe montre
				// les images des autres coloris tant qu'aucune pastille n'a été cliquée).
				MediaManager._applyMobileFilter(initialVariantId, mediaIds, featuredId, extraCount);
			});
		}
	}

	// Zoom plein écran : indépendant du type de galerie (variant ou produit)
	ZoomManager.init();

	// Legacy aliases
	window.productMediaSliders = ProductMediaSliders.instances;
	window.initializeSliderPair = (id, isVariant) => ProductMediaSliders.init(id, isVariant);
	window.destroySliderPair = (id) => ProductMediaSliders.destroy(id);
}

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.MediaManager = MediaManager;
window.ZoomManager = ZoomManager;
window.ProductMediaSliders = ProductMediaSliders;

// Initial page load (defer = DOM already parsed)
initProductMediaPage();

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	// Media-specific events
	['variant-media-switch-start', 'variant-media-switch-success', 'variant-media-switch-failed', 
		'variant-media-switch-error', 'variant-media-splide-refresh', 'variant-media-splide-reload'].forEach(event => {
		document.addEventListener(`product:${event}`, e => 
			console.log(`📸 [MEDIA] ${event}:`, e.detail)
		);
	});
}